class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };

    // https://pthree.atlassian.net/browse/FL-87
    this.searchProductTypes?.addEventListener('change', this.updateSearchFilterInput.bind(this));

    this.initPlaceholderAnimation();
    this.initMappings();
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      if (this.toggleNavigationButton) {
        this.toggleNavigationButton.classList.remove('is-show');
      }
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');

    const query = this.getQuery();
    const selectedSchool = this.searchProductTypes?.value || '';
    this.matchMappings(query);

    if (query.length === 0) {
      if (this.searchRecommendationEmpty && !selectedSchool) {
        this.searchContent.classList.add('hidden');
      } else if (selectedSchool) {
        this.searchContent.classList.remove('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.matchMappings('');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    this.matchMappings(query);
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  // setupURL() {
  //   const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
  //   let search_term = this.getQuery();
  //   if (this.searchProductTypes && this.searchProductTypes.value != '') {
  //     search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
  //   }
  //   return (
  //     url.searchParams.set('q', search_term),
  //     url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
  //     url.searchParams.set('resources[limit_scope]', 'each'),
  //     url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
  //     url
  //   );
  // }

  // https://pthree.atlassian.net/browse/FL-87
  setupURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();

    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `${this.searchProductTypes.value} ${search_term}`;
    }

    url.searchParams.set('q', search_term);
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    url.searchParams.set('resources[options][fields]', 'title,tag,product_type,variants.title');

    console.log('[🔍 debug] final predictive search URL:', url.toString());
    return url;
  }



  onChange() {
    const query = this.getQuery();
    this.matchMappings(query);
    if (query.length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          if (this.toggleNavigationButton) {
            this.toggleNavigationButton.classList.add('is-show');
          }
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      if (this.toggleNavigationButton) {
        this.toggleNavigationButton.classList.toggle('is-show', !isOpen);
      }
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      if (this.resetButton) {
        this.resetButton.classList.add(this.states.LOADING);
      }
    } else {
      this.removeAttribute('loading');
      if (this.resetButton) {
        this.resetButton.classList.remove(this.states.LOADING);
      }
      this.setAttribute('results', 'true');
    }
  }

  // https://pthree.atlassian.net/browse/FL-87
  updateSearchFilterInput() {
    const filterInput = document.getElementById('SearchFilterInput');
    if (!filterInput || !this.searchProductTypes) return;

    const selected = this.searchProductTypes.value;
    if (selected === '') {
      filterInput.disabled = true;
    } else {
      filterInput.disabled = false;
      filterInput.value = selected;
    }
  }

  initPlaceholderAnimation() {
    const raw = this.dataset.placeholderItems || '';
    const items = raw.split(',').map(s => s.trim()).filter(Boolean);
    if (!items.length) return;

    let idx = 0;
    this.input.placeholder = `Try searching for ${items[0]}...`;

    this.placeholderTimer = setInterval(() => {
      if (document.activeElement === this.input) return;
      if (this.getQuery().length > 0) return;
      idx = (idx + 1) % items.length;
      this.input.placeholder = `Try searching for ${items[idx]}...`;
    }, 3000);
  }

  initMappings() {
    this.mappingsData = null;
    this.mappingsContainer = this.querySelector('[id^="SearchMappings-"]');

    const url = this.dataset.mappingsUrl;
    if (!url || !this.mappingsContainer) return;

    fetch(url)
      .then(r => r.json())
      .then(data => { this.mappingsData = data; })
      .catch(() => {});
  }

  matchMappings(query) {
    if (!this.mappingsContainer) return;

    const selectedSchool = (this.searchProductTypes?.value || '').toLowerCase();

    if (!this.mappingsData || (!query && !selectedSchool)) {
      this.mappingsContainer.hidden = true;
      return;
    }

    const lower = query.toLowerCase();
    const MAX = 6;

    const seen = new Set();
    const matches = [];

    for (const entry of this.mappingsData) {
      if (matches.length >= MAX) break;
      if (seen.has(entry.collection)) continue;

      // When input is empty but a school is selected, show that school's suggestions
      if (!query) {
        const schoolMatch = entry.school && entry.school.toLowerCase() === selectedSchool;
        if (!schoolMatch) continue;
        seen.add(entry.collection);
        matches.push(entry);
        continue;
      }

      // School filter: if a school is selected, only match entries for that school
      if (selectedSchool && entry.school && entry.school.toLowerCase() !== selectedSchool) continue;

      const searchable = [entry.term, entry.school, entry.team, entry.category]
        .filter(Boolean)
        .map(s => s.toLowerCase());

      const hit = searchable.some(s => s.includes(lower));
      if (!hit) continue;

      seen.add(entry.collection);
      matches.push(entry);
    }

    if (!matches.length) {
      this.mappingsContainer.hidden = true;
      return;
    }

    const buildMappingUrl = (entry) => {
      const accessoriesCategories = ['hats', 'caps'];
      const dept = accessoriesCategories.includes((entry.category || '').toLowerCase())
        ? 'Accessories'
        : 'Clothing';

      const trimmedQuery = [entry.school, entry.category].filter(Boolean).join(' ');
      const params = new URLSearchParams();
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
      params.set('q', trimmedQuery);
      if (entry.school) params.set('filter.p.m.merchandising.child_store', entry.school);
      params.set('filter.p.m.custom.dept', dept);
      return `/search?${params.toString()}`;
    };

    const list = this.mappingsContainer.querySelector('.search__custom-suggestions-list');
    list.innerHTML = matches.map(entry => `
      <li>
        <a href="${buildMappingUrl(entry)}" class="search__mapping-item">
          <svg class="search__mapping-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M7.25 12.5C10.1495 12.5 12.5 10.1495 12.5 7.25C12.5 4.35051 10.1495 2 7.25 2C4.35051 2 2 4.35051 2 7.25C2 10.1495 4.35051 12.5 7.25 12.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10.96 10.96L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span class="search__mapping-body">
            <span class="search__mapping-term">${entry.term}</span>
            <span class="search__mapping-meta">${entry.school}${entry.category ? ' · ' + entry.category : ''}</span>
          </span>
          <svg class="search__mapping-arrow" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </a>
      </li>
    `).join('');

    this.mappingsContainer.hidden = false;
  }

}
customElements.define('predictive-search', PredictiveSearch);
